import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Tag,
  Button,
  Input,
  Avatar,
  Dropdown,
  Modal,
  Alert,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { usersApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";

const { Search } = Input;
const DASHBOARD_CUSTOMER_ROLE = "customer";
const FETCH_LIMIT = 500;

const initials = (u) =>
  `${u?.firstName?.[0] || ""}${u?.lastName?.[0] || ""}`.toUpperCase() || "U";
const avatarBg = "#dc2626";
const roleLabel = (role) =>
  (typeof role === "string" ? role : role?.name || "")
    .split(/[_\-.]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
const isDashboardUser = (user) =>
  ((typeof user?.role === "string" ? user.role : user?.role?.name || "") || "")
    .toLowerCase() !== DASHBOARD_CUSTOMER_ROLE;

const AdminUsers = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [deleteModal, setDeleteModal] = useState({ visible: false, user: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hiddenUserIds, setHiddenUserIds] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions
  const canViewUsers = hasPermission(userPermissions, "users.view");
  const canCreateUsers = hasPermission(userPermissions, "users.create");
  const canEditUsers = hasPermission(userPermissions, "users.edit");
  const canDeleteUsers = hasPermission(userPermissions, "users.delete");

  useEffect(() => {
    if (canViewUsers) {
      fetchUsers();
    }
  }, [search, canViewUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const query = { page: 1, limit: FETCH_LIMIT };
      if (search.trim()) query.search = search.trim();

      const response = await usersApi.getAll(query);
      const visibleUsers = (response.data || []).filter(
        (user) => isDashboardUser(user) && !hiddenUserIds.includes(user.id),
      );

      setUsers(visibleUsers);
      setFilters((current) => ({ ...current, page: 1 }));
    } catch (error) {
      console.error("Fetch users error:", error);
      message.error("Failed to load dashboard users");
    } finally {
      setLoading(false);
    }
  };

  const paginatedUsers = useMemo(() => {
    const start = (filters.page - 1) * filters.limit;
    return users.slice(start, start + filters.limit);
  }, [filters.limit, filters.page, users]);

  const handleDeleteUser = async () => {
    const { user } = deleteModal;

    if (currentUser && currentUser.id === user.id) {
      message.error("You cannot delete your own account!");
      setDeleteModal({ visible: false, user: null });
      return;
    }

    if (!canDeleteUsers) {
      message.error("You don't have permission to delete users");
      return;
    }

    try {
      setDeleteLoading(true);
      await usersApi.permanentDelete(user.id);

      setHiddenUserIds((ids) => (ids.includes(user.id) ? ids : [...ids, user.id]));
      const nextUsers = users.filter((item) => item.id !== user.id);
      setUsers(nextUsers);
      setDeleteModal({ visible: false, user: null });

      if (!nextUsers.length && filters.page > 1) {
        setFilters((f) => ({ ...f, page: f.page - 1 }));
      }

      message.success(`${user.firstName} ${user.lastName} deleted successfully`);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to delete this user");
      } else if (e.response?.status === 404) {
        message.error("User not found");
      } else if (e.response?.status === 400) {
        message.error(e.response?.data?.message || "Cannot delete this user");
      } else {
        const errorMsg =
          e.response?.data?.message ||
          e.response?.data?.error ||
          e.message ||
          "Failed to delete user";
        message.error(errorMsg);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      title: "User",
      key: "user",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar style={{ background: avatarBg, fontWeight: 700, flexShrink: 0 }} size={38}>
            {initials(r)}
          </Avatar>
          <div>
            <strong style={{ color: "#1a1d2e" }}>
              {r.firstName} {r.lastName}
            </strong>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>{r.email}</div>
            {currentUser && currentUser.id === r.id && (
              <Tag color="orange" style={{ marginTop: 4, fontSize: 10 }}>
                Current User
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v) =>
        v ? (
          <a href={`tel:${v}`} style={{ color: "#7B4D12" }}>
            {v}
          </a>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (value) => <Tag color="red">{roleLabel(value) || "Unknown"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      render: (v) => (
        <Tag color={v ? "green" : "default"} icon={v ? <CheckCircleOutlined /> : <StopOutlined />}>
          {v ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      key: "joined",
      render: (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => {
        const isCurrentUser = currentUser && currentUser.id === record.id;
        const items = [];

        if (canEditUsers) {
          items.push({
            key: "edit",
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => history.push(`/users/edit-admin/${record.id}`),
          });
        }

        if (canDeleteUsers && items.length > 0) {
          items.push({ type: "divider" });
        }

        if (canDeleteUsers) {
          items.push({
            key: "delete",
            label: isCurrentUser ? "Cannot delete yourself" : "Delete",
            icon: <DeleteOutlined />,
            danger: true,
            disabled: isCurrentUser,
            onClick: () => setDeleteModal({ visible: true, user: record }),
          });
        }

        if (items.length === 0) return null;

        return (
          <Dropdown menu={{ items }} placement="bottomRight" trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Permission check for viewing the page
  if (!canViewUsers) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              message="Access Denied"
              description="You don't have permission to view admin users."
              type="error"
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Dashboard Users</h5>
                  {canCreateUsers && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/users/add-admin")}
                    >
                      Add User
                    </Button>
                  )}
                </div>
              </div>

              <Alert
                type="info"
                showIcon
                className="mb-4"
                message="Dashboard access roles"
                description="This list includes all users whose role is not customer, so custom staff roles appear here too."
              />

              <div className="row mb-4">
                <div className="col-md-4">
                  <Search
                    placeholder="Search name or email…"
                    onSearch={(value) => setSearch(value)}
                    enterButton={<SearchOutlined />}
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              <ResponsiveTable
                columns={columns}
                dataSource={paginatedUsers}
                rowKey="id"
                loading={loading}
                rowClassName={(r) => (r.isActive === false ? "opacity-50" : "")}
                pagination={{
                  current: filters.page,
                  pageSize: filters.limit,
                  total: users.length,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50"],
                  showTotal: (t, r) => `${r[0]}–${r[1]} of ${t} dashboard users`,
                }}
                onChange={(p) =>
                  setFilters((f) => ({
                    ...f,
                    page: p.current,
                    limit: p.pageSize,
                  }))
                }
                scroll={{ x: true }}
              />

            </div>
          </div>
        </div>
      </div>

      <Modal
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#dc2626" }} />
            Delete User
          </span>
        }
        open={deleteModal.visible}
        onOk={handleDeleteUser}
        onCancel={() => setDeleteModal({ visible: false, user: null })}
        confirmLoading={deleteLoading}
        okText="Yes, Delete"
        okType="danger"
        width={420}
      >
        <Alert
          message="The user will be removed from the list and lose access immediately."
          type="warning"
          showIcon
          className="mb-3"
        />
        <p>
          Delete <strong>{deleteModal.user?.firstName} {deleteModal.user?.lastName}</strong> ({deleteModal.user?.email})?
        </p>
        {currentUser && currentUser.id === deleteModal.user?.id && (
          <Alert
            message="You are trying to delete your own account! This will log you out."
            type="error"
            showIcon
            className="mt-2"
          />
        )}
      </Modal>
    </div>
  );
};

export default AdminUsers;